<template>
    <section name="NoticeDetail">
        <information-modal
            :title="`Notice Details - ${props.item.description}`"
            :show-modal="showNoticeDetailsModal"
            :show-footer="true"
            width="38.5em"
            @dismiss="handleNoticeDetailsModalDismiss()">
            <template #body>
                <div class="container-fluid">
                    <InformationModal
                        title="Dismiss Confirmation"
                        :show-footer="true"
                        :show-modal="showModal"
                        @dismiss="handleModalDismiss">
                        <template v-slot:body>
                            <span v-if="uniqueRecipientAgencyCount > 1"
                                >Notice will be dismissed for all authorized recipients. Are you sure you want to
                                dismiss this notice?</span
                            >
                            <span v-else
                                >Are you sure you want to dismiss this notice for
                                {{ oriLabel ?? props.item.ori }}?</span
                            >
                        </template>
                        <template v-slot:footer>
                            <div class="hstack gap-2">
                                <button
                                    type="button"
                                    class="btn btn-primary w-50"
                                    @click.prevent="submitDismiss()">
                                    Ok
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-secondary w-50"
                                    @click.prevent="handleModalDismiss()">
                                    Cancel
                                </button>
                            </div>
                        </template>
                    </InformationModal>
                    <div class="">
                        <div class="row">
                            <div class="col-2">
                                <label class="fw-bold">Agency:</label>
                            </div>
                            <div class="col">
                                {{ oriLabel ?? props.item.ori }}
                            </div>
                        </div>
                        <div class="row mt-2">
                            <div class="col-2">
                                <label
                                    id="lblDate"
                                    class="fw-bold"
                                    >Date:</label
                                >
                            </div>
                            <div class="col">
                                {{ props.item.run_date }}
                            </div>
                        </div>
                        <div class="row mt-2">
                            <div class="col">
                                <label
                                    for="body"
                                    aria-label="Main content of the notice"
                                    class="fw-bold"
                                    >Body:</label
                                >
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-11 offset-1">
                                <p
                                    class="border border-1 rounded bg-light p-2"
                                    style="min-height: 100px">
                                    {{ props.item.message }}
                                </p>
                            </div>
                        </div>
                        <div
                            id="divRecipients"
                            class="mt-2">
                            <div
                                class="row"
                                v-if="uniqueRecipientAgencyCount == 1">
                                <div class="col-4">
                                    <label
                                        id="lblRecipients"
                                        class="align-self-baseline fw-bold"
                                        >Email Recipient(s):</label
                                    >
                                </div>
                                <div class="col-8">
                                    {{ emailLabel }}
                                </div>
                            </div>
                            <div
                                class="container-fluid p-0"
                                v-else>
                                <div class="row">
                                    <div class="col">
                                        <label
                                            id="lblRecipients"
                                            class="fw-bold">
                                            Email Recipient(s):
                                        </label>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-11 offset-1">
                                        <b-grid
                                            caption="Recipient list"
                                            class="recipient-grid"
                                            :items="noticeStore.recipients"
                                            :columns="columns"
                                            :filterableFields="noticeStore.recipientFilterableFields"
                                            :filterOptions="noticeStore.recipientFilterOptions"
                                            :tableOptions="noticeStore.recipientTableOptions"
                                            :show-filter="true"
                                            :show-paging="false"
                                            @option-change="noticeStore.getNoticeRecipients(props.item.id)">
                                            <template #header.is_dismissed="{column}">
                                                <div class="w-100 text-center">
                                                    {{ column.text }}
                                                </div>
                                            </template>
                                            <template #cell.is_dismissed="{item, column}">
                                                <div class="w-100 text-center">
                                                    <input
                                                        class=""
                                                        disabled="true"
                                                        readonly
                                                        type="checkbox"
                                                        :checked="item.is_dismissed" />
                                                </div>
                                            </template>
                                        </b-grid>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="row mt-2">
                    <div class="col-sm-12">
                        <div class="hstack gap-2 align-end">
                            <button
                                type="button"
                                class="btn btn-secondary"
                                data-bs-dismiss="modal"
                                @click.prevent="handleNoticeDetailsModalDismiss()">
                                Cancel
                            </button>
                            <button
                                id="btnDismiss"
                                class="btn btn-primary confirm-remove margin-right-5"
                                @click.prevent="showDismissModal()"
                                :disabled="!isDismissable"
                                v-if="hasPermission('DismissNotices')">
                                Dismiss Notice
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </information-modal>
    </section>
</template>
<script async setup lang="ts">
import {computed, ref, onMounted, watch} from "vue";
import {BTableColumn, BTableOptions} from "../BTable/BTable.types";
import {BFilter, BFilterableField} from "../BFilterEditor/BFilterEditor.types";
import BGrid from "../BGrid/BGrid.vue";
import {useNoticeStore} from "@/stores/noticeStore";
import {NoticeItem} from "./types";
import InformationModal from "../Misc/InformationModal.vue";
import {useOriStore} from "../../stores/oriStore";
import {Notice} from "@/types";
import hasPermission from "@/composable/permissionHelper";

const props = defineProps<{
    item: NoticeItem;
    ori?: string;
    notification?: Notice;
    showNoticeDetailsModal: boolean;
}>();
const emit = defineEmits(["submit", "dismiss"]);

const noticeStore = useNoticeStore();
const oriStore = useOriStore();
oriStore.dataset = undefined;
var showModal = ref(false);
var oriLabel = ref(undefined as string | undefined);
var emailLabel = ref("");
var uniqueRecipientAgencyCount = ref(0);

const columns = [
    {key: "agency", text: "Agency", sortable: false, value: (item) => item.agency},
    {key: "email", text: "Email", sortable: false, value: (item) => item.email, class: (item) => ["column-wrap"]},
    {
        key: "is_dismissed",
        text: "Dismissed",
        sortable: false,
        value: (item) => item.is_dismissed,
        columnClass: ["text-wrap"],
        class: (item) => ["text-wrap"],
    },
] as BTableColumn[];

var isDismissable = computed(() => {
    if (props.notification != undefined) {
        return !props.notification?.is_dismissed;
    }

    return !props.item.is_dismissed;
});

function getAgencyLabel() {
    if (props.ori != undefined) {
        oriStore.getOri(props.ori).then(() => {
            oriLabel.value = oriStore.ori?.ori_name ?? "";
        });
    } else {
        oriLabel.value = undefined;
    }
}

async function getRecipients() {
    if (props.item.id) {
        await noticeStore.getNoticeRecipients(props.item.id);

        uniqueRecipientAgencyCount.value = getDistinctCount(noticeStore.recipients.map((m) => m.agency));

        if (uniqueRecipientAgencyCount.value == 1) {
            let ori = props.ori;

            if (!ori) {
                let agencyLabel = noticeStore.recipients[0].agency;
                ori = agencyLabel.substring(agencyLabel.indexOf("(") + 1, agencyLabel.length - 1);
            }

            emailLabel.value = await noticeStore.getNoticeRecipientsForOriAsString(props.item.id, ori);
        }
    }
}

function getDistinctCount(array: string[]): number {
    return new Set(array).size;
}

function showDismissModal() {
    showModal.value = true;
}

async function submitDismiss() {
    if (props.notification != undefined) {
        await noticeStore.dismissNotice(props.notification.id);
    } else {
        await noticeStore.dismissNoticesByLogId(props.item.id, undefined);
    }

    if (props.ori) {
        noticeStore.list(props.ori);
    }

    showModal.value = false;
    emit("submit");
}

function handleModalDismiss() {
    showModal.value = false;
}

function handleNoticeDetailsModalDismiss() {
    emit("dismiss");
}

function loadData() {
    noticeStore.resetRecipients();

    if (props.ori) {
        noticeStore.recipientFilterOptions = {filters: [{property: "agency", operation: "eq", value: props.ori}]};
    }

    getRecipients();
    getAgencyLabel();
}

watch(
    () => props.item.id,
    (val) => {
        loadData();
    }
);

watch(
    () => props.ori,
    (val) => {
        loadData();
    }
);
</script>
<style lang="css">
.width-120 {
    width: 120px;
}

.recipient-grid {
    overflow: auto;
    height: 25rem;
}
</style>
